export type PlanejamentoSituacaoAprovacao =
  | 'EM_ELABORACAO'
  | 'APROVADO'
  | 'AGUARDANDO_APROVACAO'
  | 'EM_ANALISE'
  | 'AGUARDANDO_CORRECAO';

export interface ItemEducacionalAPI {
  id: number;
  descricao?: string;
  nome?: string;
  filhos?: Array<{ id?: number; descricao?: string; nome?: string }>;
}

export interface PlanejamentoConteudoItem {
  descricao?: string | null;
  valor?: string | null;
  [key: string]: unknown;
}

export interface PlanejamentoArquivoItem {
  id?: number;
  nome?: string | null;
  descricao?: string | null;
  url?: string | null;
  link?: string | null;
  tipo?: string | null;
  tamanho?: number | null;
  [key: string]: unknown;
}

export interface PlanejamentoAulaHorario {
  id?: number;
  dataAula?: string | null;
  data?: string | null;
  inicioAula?: string | null;
  horaInicio?: string | null;
  horarioInicio?: string | null;
  fimAula?: string | null;
  horaFim?: string | null;
  horarioFim?: string | null;
  numeroAula?: number | null;
  numero?: number | null;
  [key: string]: unknown;
}

export interface PlanejamentoItemConteudo {
  id?: number;
  turma?: { id?: number; descricao?: string; nome?: string } | null;
  itensEducacionais?: Array<{
    itemEducacional?: { id?: number; descricao?: string; nome?: string } | null;
  }> | null;
  aulasPlanejadas?: PlanejamentoAulaHorario[] | null;
  aulas?: PlanejamentoAulaHorario[] | null;
  expandido?: boolean;
  expandirAulas?: boolean;
  [key: string]: unknown;
}

export interface PlanejamentoObservacaoItem {
  id?: number;
  observacao?: string | null;
  situacao?: 'ATIVO' | 'INATIVO' | string | null;
  createdBy?: string | null;
  createdIn?: string | null;
  expandido?: boolean;
  [key: string]: unknown;
}

export interface PlanejamentoAulaApiItem {
  id: number;
  tema: string;
  servidor: { id: number; nome: string };
  dataInicio: string;
  dataFim?: string;
  situacaoAprovacao?: PlanejamentoSituacaoAprovacao | null;
  observacao?: string | null;
  componente?: { id?: number; nome?: string; descricao?: string } | null;
  turmas?: Array<{ id?: number; descricao?: string; nome?: string }> | null;
  itensEducacionais?: Array<{
    itemEducacional: ItemEducacionalAPI;
    conteudos?: PlanejamentoConteudoItem[] | null;
  }> | null;
  itemEducacional?: ItemEducacionalAPI | null;
  itensConteudos?: PlanejamentoItemConteudo[] | null;
  links?: Array<{ id?: number; url?: string; link?: string; descricao?: string }> | null;
  arquivos?: PlanejamentoArquivoItem[] | null;
  anexos?: PlanejamentoArquivoItem[] | null;
  anp?: string | null;
  cargaHoraria?: string | number | null;
  horas?: number | null;
  minutos?: number | null;
  isDevolutiva?: boolean | null;
  objetivosGerais?: string | null;
  objetivosEspecificos?: string | null;
  metodologiaUtilizada?: string | null;
  praticasPedagogicas?: string | null;
  recursosDidaticos?: string | null;
  referenciasBibliograficas?: string | null;
  observacoesAprovacaoConteudos?: PlanejamentoObservacaoItem[] | null;
  [key: string]: unknown;
}

export interface PlanejamentoAulaApiResponse {
  content: PlanejamentoAulaApiItem[];
  total?: number;
  offset?: number;
  limit?: number;
  hasNext?: boolean;
}


export interface PlanejamentoCampoAdicionalItem {
  campo?: { id?: number; nome?: string; descricao?: string; ordem?: number } | null;
  valor?: string | null;
  texto?: string | null;
  descricao?: string | null;
  nome?: string | null;
  ordem?: number;
}

export type PlanejamentoCamposAdicionaisRaw =
  | PlanejamentoCampoAdicionalItem[]
  | { content?: PlanejamentoCampoAdicionalItem[]; dataFim?: string | null; [key: string]: unknown };

export function normalizarCamposAdicionais(
  raw: PlanejamentoCamposAdicionaisRaw | null | undefined
): PlanejamentoCampoAdicionalItem[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  const chavesWrapper = ['content', 'conteudos', 'campos', 'items', 'data', 'registros'];
  for (const chave of chavesWrapper) {
    const val = (raw as Record<string, unknown>)[chave];
    if (Array.isArray(val)) return val as PlanejamentoCampoAdicionalItem[];
  }
  return [];
}

export type PlanejamentoStatus = 'EM_ELABORACAO' | 'PUBLICADO' | 'ARQUIVADO';

export interface PeriodoAvaliativoPlanejamento {
  id: number;
  descricao: string;
  dataInicial: string;
  dataFinal: string;
}

export interface PlanejamentoFiltros {
  termo?: string;
  data?: string;
  situacao?: PlanejamentoSituacaoAprovacao;
  dataInicioPeriodo?: string;
  dataFimPeriodo?: string;
}

export interface PlanejamentoAula {
  id: number;
  titulo: string;
  autorNome: string;
  data: string;
  dataFim?: string;
  status: PlanejamentoStatus;
  situacaoAprovacaoRaw: PlanejamentoSituacaoAprovacao | null;
  componente?: string;
  disciplinaExibicao: string;
  observacao?: string;
  disciplinasCalculadas?: string;
}


export const PLANEJAMENTO_SITUACAO_LABELS: Record<PlanejamentoSituacaoAprovacao, string> = {
  EM_ELABORACAO:       'Em elaboração',
  APROVADO:            'Aprovado',
  AGUARDANDO_APROVACAO:'Aguardando análise',
  EM_ANALISE:          'Em análise',
  AGUARDANDO_CORRECAO: 'Aguardando correção'
};

// ─── Mapper: API → view model ────────────────────────────────────────────────

/**
 * Converte item da API para view model. `disciplinasMap` é buscado uma única vez
 * (/context/turma/{id}/itens-educacionais) e reutilizado em todos os cards.
 */
export function mapApiItemToPlanejamento(
  item: PlanejamentoAulaApiItem,
  disciplinasMap?: Map<number, string>
): PlanejamentoAula {
  const situacao: PlanejamentoSituacaoAprovacao | null = item.situacaoAprovacao ?? null;

  let componente: string | undefined =
    item.componente?.nome ?? item.componente?.descricao ?? undefined;

  if (!componente) {
    const ieWrapper = item.itensEducacionais?.[0];
    if (ieWrapper?.itemEducacional) {
      const ie = ieWrapper.itemEducacional;
      componente =
        ie.descricao ||
        ie.filhos?.[0]?.descricao ||
        (disciplinasMap ? disciplinasMap.get(ie.id) : undefined) ||
        undefined;
    }
  }

  if (!componente && item.itemEducacional) {
    const ie = item.itemEducacional;
    componente =
      ie.descricao ||
      ie.filhos?.[0]?.descricao ||
      (disciplinasMap ? disciplinasMap.get(ie.id) : undefined) ||
      undefined;
  }

  return {
    id: item.id,
    titulo: item.tema ?? '',
    autorNome: item.servidor?.nome ?? '',
    data: item.dataInicio ?? '',
    dataFim: item.dataFim ?? undefined,
    status: situacao === 'APROVADO' ? 'PUBLICADO' : 'EM_ELABORACAO',
    situacaoAprovacaoRaw: situacao,
    componente,
    disciplinaExibicao: componente ?? '',
    observacao: item.observacao ?? undefined
  };
}
